import type { Rule } from "antd/es/form";

/**
 * Reusable antd `Form.Item` validators. Each function returns a `Rule` so it
 * composes naturally with the rest of the `rules` array:
 *
 *   import { Form } from "components/shared";
 *
 *   <Form.Item
 *     name="confirmEmail"
 *     dependencies={["email"]}
 *     rules={[
 *       { required: true, message: "Confirm your email" },
 *       Form.validators.matchesField("email", "Emails do not match"),
 *     ]}
 *   >
 *     <Input />
 *   </Form.Item>
 */

/**
 * Field must equal another field's current value (e.g. confirm email/password).
 *
 * Pair with `dependencies={[otherFieldName]}` on the `Form.Item` so this field
 * re-validates whenever the dependency changes.
 */
export const matchesField =
  (otherFieldName: string, message: string): Rule =>
  ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value || getFieldValue(otherFieldName) === value) {
        return Promise.resolve();
      }
      return Promise.reject(new Error(message));
    },
  });

/**
 * Required boolean — passes only when the value is exactly `true`.
 *
 * antd's built-in `{ required: true }` doesn't enforce `true` for booleans
 * (any non-undefined value passes). Use this for "must accept terms" toggles.
 */
export const mustBeTrue = (message: string): Rule => ({
  validator(_, value: unknown) {
    return value === true
      ? Promise.resolve()
      : Promise.reject(new Error(message));
  },
});

type NumericRangeOptions = {
  min?: number;
  max?: number;
  /** When true, rejects fractional values. */
  integer?: boolean;
  /**
   * Override the default error messages. If omitted, sensible defaults are
   * used (e.g. "Must be ≥ 18", "Must be a number"). Pass a single string to
   * use the same message for all failures.
   */
  message?: string;
};

/**
 * Numeric range validator that handles both `string` and `number` field
 * values. Useful when the input is a text `Input` whose value comes back as
 * a string (the typical case for `Form` + `<Input type="number">`).
 *
 *   rules={[
 *     { required: true, message: "Age is required" },
 *     Form.validators.numericRange({ min: 18, max: 120 }),
 *   ]}
 */
export const numericRange = ({
  min,
  max,
  integer = false,
  message,
}: NumericRangeOptions): Rule => ({
  validator(_, rawValue: unknown) {
    if (rawValue === undefined || rawValue === null || rawValue === "") {
      return Promise.resolve();
    }
    const n =
      typeof rawValue === "number" ? rawValue : Number(rawValue as string);
    if (Number.isNaN(n)) {
      return Promise.reject(new Error(message ?? "Must be a number"));
    }
    if (integer && !Number.isInteger(n)) {
      return Promise.reject(new Error(message ?? "Must be an integer"));
    }
    if (min !== undefined && n < min) {
      return Promise.reject(new Error(message ?? `Must be ≥ ${min}`));
    }
    if (max !== undefined && n > max) {
      return Promise.reject(new Error(message ?? `Must be ≤ ${max}`));
    }
    return Promise.resolve();
  },
});

/**
 * Reject values that are only whitespace. `{ required: true }` treats
 * `"   "` as a valid value because it's a non-empty string — pair this rule
 * with `required` for text fields where blank-only input shouldn't pass.
 *
 * Empty string (`""`) and non-string values are skipped (resolved). Empty
 * means "not filled" — let `{ required: true }` own that case so cleared
 * inputs don't double-fire (`"required"` AND `"no whitespace"` errors).
 */
export const noWhitespace = (message: string): Rule => ({
  validator(_, value: unknown) {
    if (
      typeof value !== "string" ||
      value === "" ||
      value.trim().length > 0
    ) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(message));
  },
});

/**
 * Absolute http(s) URL. `new URL("example.com")` throws (relative), which
 * is the failure mode we want — a scheme-less string would otherwise land
 * on AOA as a broken Custom URL fallback.
 *
 * Empty string / non-strings resolve so `{ required: true }` owns "not
 * filled" and `noWhitespace` owns `"   "`.
 */
export const isHttpUrl = (value: unknown): boolean => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

export const httpUrl = (message: string): Rule => ({
  validator(_, value: unknown) {
    if (
      typeof value !== "string" ||
      value === "" ||
      value.trim() === ""
    ) {
      return Promise.resolve();
    }
    return isHttpUrl(value)
      ? Promise.resolve()
      : Promise.reject(new Error(message));
  },
});

export const validators = {
  matchesField,
  mustBeTrue,
  numericRange,
  noWhitespace,
  httpUrl,
};
